import React, { ReactNode, useEffect, useMemo, useState } from "react";
import produce from "immer";
import { WritableDraft } from "immer/dist/internal";
import { DateTime } from "luxon";
import { startOfUSWeek } from 'services/Utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';

import { Button, CalendarNoControls, DatePicker, ErrorBoundary, ErrorMessage, EventEdgeStyle, ICalendarCell, ICalendarEvent, ICalendarRenderEventCallback, ICalendarRenderDayPanelEventCallback, Loader } from "components";
import { AsyncStatus } from "enums";
import { useLuxonTimezone, useStale } from "hooks";
import './Calendar.scss';
import { applyTimezone, unapplyTimezone } from "services/Utils";

export type CalendarView = 'month' | 'agenda';

interface ICalendarState<TData> {
    events: ICalendarEvent<TData>[],
    loadError?: any,
    loadPromise?: Promise<ICalendarEvent<TData>[]>,
    loadStatus: AsyncStatus,
    selectedDate?: Date,
    showDayPanel?: boolean,
    view: CalendarView,
}

export interface ICalendarProps<TData> {
    className?: string,
    dateEnd: Date,
    dateStart: Date,
    defaultEventEdgeStyle?: EventEdgeStyle,
    defaultSelectedDate?: Date,
    defaultView?: CalendarView,
    groupGapToleranceMinutes?: number,
    timezone?: string,

    onChangeSelectedDate?: (selectedDate: Date) => void,
    onChangeView?: (view: CalendarView) => void,
    onClickCell?: (cell: ICalendarCell) => void,
    onClickMoreButton?: (cell: ICalendarCell) => void,
    onLoadEvents: (dateStart: Date, dateEnd: Date) => Promise<ICalendarEvent<TData>[]>,
    onNavigate: (dateStart: Date, dateEnd: Date) => void,
    renderAgenda: (events: ICalendarEvent<TData>[]) => ReactNode,
    renderAgendaLoader?: (loadPromise: Promise<any>) => ReactNode,
    renderError?: (err: any) => ReactNode,
    renderEvent: ICalendarRenderEventCallback<TData>,
    renderEventDayPanel: ICalendarRenderDayPanelEventCallback<TData>,
}

export function Calendar<TData>({
    className,
    dateEnd,
    dateStart,
    defaultEventEdgeStyle,
    defaultSelectedDate,
    defaultView,
    groupGapToleranceMinutes,
    timezone: propsTimezone,

    onChangeSelectedDate,
    onChangeView,
    onClickCell,
    onClickMoreButton,
    onLoadEvents,
    onNavigate,
    renderAgenda,
    renderAgendaLoader,
    renderError,
    renderEvent,
    renderEventDayPanel,
}: ICalendarProps<TData>) {
    let [{
        events,
        loadError,
        loadPromise,
        loadStatus,
        selectedDate,
        showDayPanel,
        view,
    }, setState] = useState<ICalendarState<TData>>({
        events: [],
        loadStatus: AsyncStatus.Loading,
        selectedDate: defaultSelectedDate,
        view: defaultView || 'month',
    })

    const { timezone } = useLuxonTimezone(propsTimezone);

    if (view !== 'month' && view !== 'agenda') {
        console.warn('Calendar: invalid view prop: ' + view);
        view = 'month';
    }

    const dateStartDt = useMemo(() => DateTime.fromJSDate(dateStart, {zone: timezone}), [dateStart]);
    const dateEndDt = useMemo(() => DateTime.fromJSDate(dateEnd, {zone: timezone}), [dateEnd]);

    // Fetch events for all days shown on the calendar, not just the current month.
    // This will be a few extra days at the start and end of the month.
    function standardizeRequestDatesMonth(start: DateTime): [newStart: DateTime, newEnd: DateTime] {
        const newStart = startOfUSWeek(start.startOf('month'));
        const newEnd = start.endOf('month').endOf('week');
        return [newStart, newEnd];
    }

    // For agenda view, use the dates we were passed, but still adjust them to month boundaries.
    function standardizeRequestDatesAgenda(start: DateTime, end: DateTime): [newStart: DateTime, newEnd: DateTime] {
        if (end < start) {
            end = start;
        }

        const newStart = start.startOf('month');
        const newEnd = end.endOf('month');
        return [newStart, newEnd];
    }

    // When we request data, we want to request at month boundaries regardless of our input.
    function standardizeRequestDates(newView: CalendarView, newStart: DateTime, newEnd: DateTime): [newStart: DateTime, newEnd: DateTime] {
        if (newView === 'month') return standardizeRequestDatesMonth(newStart);
        if (newView === 'agenda') return standardizeRequestDatesAgenda(newStart, newEnd);

        console.error('Standardizing dates not implemented for view: ' + newView);
        return [newStart, newEnd];
    }

    // Load events when our dates change. However, we only need to load if the request dates change.
    // For example, if we're in "month" view, then we ignore the end date and just load the current month.
    const requestDates = useMemo(() => {
        const [start, end] = standardizeRequestDates(view, dateStartDt, dateEndDt);
        return {
            start: start,
            end: end,
            // We'll only load again if the date changes. We don't care about the time.
            startDateString: start.toISODate(),
            endDateString: end.toISODate(),
        }
    }, [dateStartDt, dateEndDt, view]);

    const requestStale = useStale();

    useEffect(() => {
        const staleId = requestStale.startAsyncCall();
        const p = onLoadEvents(requestDates.start.toJSDate(), requestDates.end.toJSDate());

        setState(produce(draft => {
            draft.loadStatus = AsyncStatus.Loading;
            draft.loadError = undefined;
            draft.loadPromise = p;
            draft.events = [];
        }));

        p.then((newEvents) => {
            if (requestStale.isStale(staleId)) return;

            setState(produce(draft => {
                draft.loadStatus = AsyncStatus.Success;
                draft.events = newEvents as WritableDraft<ICalendarEvent<TData>>[];
            }));
        }).catch((err) => {
            if (requestStale.isStale(staleId)) return;

            setState(produce(draft => {
                draft.loadStatus = AsyncStatus.Error;
                draft.loadError = err;
            }));
        });
    }, [requestDates.startDateString, requestDates.endDateString])

    function onClickToday() {
        const newSelectedDateDt = DateTime.now().setZone(timezone).startOf('day')
        const newSelectedDate = newSelectedDateDt.toJSDate();
        setState(produce(draft => {
            draft.selectedDate = newSelectedDate;
        }));

        const newStart = newSelectedDateDt.startOf('month');
        let newEnd = dateEndDt;
    
        if (view === 'month') {
            newEnd = newStart.endOf('month');
        } else if (view === 'agenda') {
            // Reset duration to 6 months
            newEnd = newStart.plus({months: 6}).endOf('month')
        } else {
            console.error('Need to implement "today" for view: ' + view);
        }

        onNavigate(newStart.toJSDate(), newEnd.toJSDate());
        onChangeSelectedDate && onChangeSelectedDate(newSelectedDate);
    }

    function onClickNext() {
        moveDates(1);
    }

    function onClickPrev() {
        moveDates(-1);
    }

    function moveDates(diffInMonths: number) {
        const newStart = dateStartDt.plus({months: diffInMonths}).startOf('month');
        let newEnd = dateEndDt;

        if (view === 'month') {
            newEnd = newStart.endOf('month');
        } else if (view === 'agenda') {
            newEnd = dateEndDt.plus({ months: diffInMonths }).endOf('month');
        } else {
            console.error('Need to implement moveDates for view: ', view);
        }

        // Since we changed months, we need to move the selectedDate into the current month so that
        // we have data to show for it in the day panel. This is what outlook calendar does.
        const newSelectedDate = newStart.toJSDate();
        setState(produce(draft => {
            draft.selectedDate = newSelectedDate;
        }));

        onNavigate(newStart.toJSDate(), newEnd.toJSDate());
        onChangeSelectedDate && onChangeSelectedDate(newSelectedDate);
    }

    function onStartDateChange(newStart: Date) {
        // AntD pickers don't support timezones, so we need to convert from local time.
        let newStartDt = unapplyTimezone(newStart, timezone);
        let newEndDt = dateEndDt;

        // If the end is now before the start, we need to push the end further out
        if (newEndDt < newStartDt) {
            newEndDt = newStartDt;
        }
        newStartDt = newStartDt.startOf('month');
        newEndDt = newEndDt.endOf('month');

        // We need to keep the selected date inside the current month
        const newSelectedDate = newStartDt.toJSDate();
        setState(produce(draft => {
            draft.selectedDate = newSelectedDate;
        }));

        onNavigate(newStartDt.toJSDate(), newEndDt.toJSDate());
        onChangeSelectedDate && onChangeSelectedDate(newSelectedDate);
    }

    function onEndDateChange(newEnd: Date) {
        let newStartDt = dateStartDt;
        // AntD pickers don't support timezones, so we need to convert from local time.
        let newEndDt = unapplyTimezone(newEnd, timezone);

        // If the start is now after the end, move the start to before.
        if (newStartDt > newEndDt) {
            newStartDt = newEndDt;
        }
        newStartDt = newStartDt.startOf('month');
        newEndDt = newEndDt.endOf('month');

        // We need to keep the selected date inside the current month
        const newSelectedDate = newStartDt.toJSDate();
        setState(produce(draft => {
            draft.selectedDate = newSelectedDate;
        }));

        onNavigate(newStartDt.toJSDate(), newEndDt.toJSDate());
        onChangeSelectedDate && onChangeSelectedDate(newSelectedDate);
    }

    function onChangeViewInternal(e: React.ChangeEvent<HTMLSelectElement>) {
        const newView = e.target.value as CalendarView;
        setState(produce(draft => {
            draft.view = newView;
        }))
        onChangeView && onChangeView(newView);
    }

    function onClickCellInternal(cell: ICalendarCell) {
        onClickCell && onClickCell(cell);
        onSelectCellInternal(cell);
    }

    function onClickMoreButtonInternal(cell: ICalendarCell) {
        onClickMoreButton && onClickMoreButton(cell);
        onSelectCellInternal(cell);
    }

    function onSelectCellInternal(cell: ICalendarCell) {
        const newSelectedDate = cell.dateDt.toJSDate();
        setState(produce(draft => {
            draft.selectedDate = newSelectedDate;
            draft.showDayPanel = true;
        }));
        onChangeSelectedDate && onChangeSelectedDate(newSelectedDate);
    }

    function onCloseDayPanel() {
        setState(produce(draft => {
            draft.showDayPanel = false;
        }));
    }

    const datePickerFormat = 'MMM YYYY';

    // Ant design doesn't support timezones for its pickers, so we need to convert to the browser's timezone.
    const appliedTimezoneStart = useMemo(() => applyTimezone(dateStartDt), [dateStartDt]);
    const appliedTimezoneEnd = useMemo(() => applyTimezone(dateEndDt), [dateEndDt]);

    return <div className={cn("calendarWithControls", className)}>
        <div className="calendarWithControls_controls">
            <Button theme="blueOutline" size="small" className="calendarWithControls_today" onClick={onClickToday}>
                Today
            </Button>
            <Button theme="icon" size="small" className="calendarWithControls_prev" onClick={onClickPrev}>
                <FontAwesomeIcon icon="chevron-left" />
            </Button>
            <Button theme="icon" size="small" className="calendarWithControls_next" onClick={onClickNext}>
                <FontAwesomeIcon icon="chevron-right" />
            </Button>
            <DatePicker
                getPopupContainer={(trig) => trig.parentNode as HTMLElement}
                value={appliedTimezoneStart}
                onChange={onStartDateChange}
                picker="month"
                format={datePickerFormat}
                inputReadOnly={true}
                suffixIcon={null}
                allowClear={false}
                className="calendarWithControls_datepicker calendarWithControls_dateStart"
            />
            {view === 'agenda' && (<>
                <div className="calendarWithControls_datepickerSeparator">
                    -
                </div>
                <DatePicker
                    getPopupContainer={(trig) => trig.parentNode as HTMLElement}
                    value={appliedTimezoneEnd}
                    onChange={onEndDateChange}
                    picker="month"
                    format={datePickerFormat}
                    inputReadOnly={true}
                    suffixIcon={null}
                    allowClear={false}
                    className="calendarWithControls_datepicker calendarWithControls_dateEnd"
                />
            </>)}
            <div className="calendarWithControls_controlsSpacer"></div>
            <div className="calendarWithControls_rightControls">
                <select
                    className="calendarWithControls_viewSelect"
                    onChange={onChangeViewInternal}
                    value={view}
                >
                    <option value={'month'}>Month</option>
                    <option value={'agenda'}>Agenda</option>
                </select>
            </div>
        </div>
        <ErrorBoundary>
            {loadStatus === AsyncStatus.Error && <>
                {renderError && renderError(loadError)}
                {!renderError && <ErrorMessage message={loadError + ''} />}
            </>}
            {view === 'month' && (
                <div className="calendarWithControls_calendar">
                    <CalendarNoControls
                        date={dateStart}
                        defaultEventEdgeStyle={defaultEventEdgeStyle}
                        events={events}
                        groupGapToleranceMinutes={groupGapToleranceMinutes}
                        isLoading={loadStatus === AsyncStatus.Loading}
                        onClickCell={onClickCellInternal}
                        onClickMoreButton={onClickMoreButtonInternal}
                        onCloseDayPanel={onCloseDayPanel}
                        renderEvent={renderEvent}
                        renderEventDayPanel={renderEventDayPanel}
                        selectedDate={selectedDate}
                        showDayPanel={showDayPanel}
                        timezone={timezone}
                    />
                </div>
            )}
            {view === 'agenda' && <>
                <div className="calendarWithControls_agenda">
                    {loadStatus === AsyncStatus.Loading && <>
                        {renderAgendaLoader && renderAgendaLoader(loadPromise)}
                        {!renderAgendaLoader && <Loader />}
                    </>}
                    {loadStatus === AsyncStatus.Success && renderAgenda(events)}
                </div>
            </>}
        </ErrorBoundary>
    </div>
}